/**
 * Lesson *view* — renders the engine's state into the tutor pane and wires its
 * controls back to the engine. Two modes, mirroring the engine's state:
 *
 *   - **menu**  — a list of chapters with a `done/total` count and the `*`
 *                 completion marker, modelled on the original menu-driven
 *                 lesson list. Clicking a chapter opens it.
 *   - **step**  — the open chapter's current step: story framing, explanation,
 *                 a "try it" prompt, and navigation (back to menu, prev, next)
 *                 plus an "I did it!" button that marks the step done.
 *
 * It subscribes to `engine.onChange` and re-renders on every change, so any
 * navigation/completion — whether triggered here or by the immediate window
 * (#701) via `engine.submitExercise` — keeps the pane in sync. DOM-free under
 * test: a fake `Document` records the tree and click handlers.
 */
import type { LessonEngine } from "./engine";

/** Completion marker shown beside a finished chapter (the original `*`). */
export const COMPLETE_MARKER = "*";

/** Public surface for mounting the lesson UI into a host element. */
export interface LessonView {
  /** Render (and keep re-rendering) the lesson UI into `container`. */
  mount(container: HTMLElement): void;
}

/** Create a lesson view bound to an engine. */
export function createLessonView(
  doc: Document,
  engine: LessonEngine,
): LessonView {
  let container: HTMLElement | null = null;
  let unsubscribe: (() => void) | null = null;

  const el = (tag: string, className?: string, text?: string): HTMLElement => {
    const node = doc.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  /* ------------------------------- menu --------------------------------- */

  function renderMenu(): HTMLElement {
    const root = el("section", "lessons lessons--menu");
    root.dataset.view = "menu";
    root.append(el("h2", "lessons__title", "Choose a lesson"));

    const list = el("ul", "lessons__list");
    for (const chapter of engine.chapters) {
      const status = engine.chapterStatus(chapter.id);

      const item = el("li", "lessons__item");
      // A button per chapter so it's keyboard-accessible and clickable.
      const btn = doc.createElement("button");
      btn.className = "lessons__chapter";
      btn.setAttribute("type", "button");
      btn.dataset.chapterId = chapter.id;
      btn.dataset.complete = String(status.complete);
      btn.dataset.action = "open";

      // Marker column: the `*` when complete, a hollow bullet otherwise.
      const marker = el(
        "span",
        "lessons__marker",
        status.complete ? COMPLETE_MARKER : "·",
      );
      marker.setAttribute("aria-hidden", "true");

      const title = el("span", "lessons__chapter-title", chapter.title);
      const count = el(
        "span",
        "lessons__count",
        `${status.done}/${status.total}`,
      );

      btn.append(marker, title, count);
      btn.addEventListener("click", () => engine.openChapter(chapter.id));
      item.append(btn);
      list.append(item);
    }
    root.append(list);
    return root;
  }

  /* ------------------------------- step --------------------------------- */

  function renderStep(): HTMLElement {
    const chapter = engine.currentChapter!;
    const step = engine.currentStep!;
    const index = engine.currentStepIndex;
    const total = chapter.steps.length;
    const status = engine.chapterStatus(chapter.id);

    const root = el("section", "lessons lessons--step");
    root.dataset.view = "step";
    root.dataset.chapterId = chapter.id;
    root.dataset.stepId = step.id;

    // Header: back-to-menu + chapter title + step counter.
    const header = el("header", "lessons__step-header");
    const back = doc.createElement("button");
    back.className = "lessons__back";
    back.setAttribute("type", "button");
    back.dataset.action = "back";
    back.textContent = "‹ Lessons";
    back.addEventListener("click", () => engine.closeChapter());

    header.append(
      back,
      el("h2", "lessons__title", chapter.title),
      el("p", "lessons__progress", `Step ${index + 1} of ${total}`),
    );
    root.append(header);

    // Story framing + goal payoff for context.
    root.append(el("p", "lessons__story", chapter.story));
    root.append(el("p", "lessons__goal", `🎯 ${chapter.goal}`));

    // The step body: explanation + hands-on prompt.
    root.append(el("p", "lessons__explanation", step.explanation));

    const tryIt = el("div", "lessons__tryit");
    tryIt.dataset.slot = "tryit"; // #701 reads the prompt / wires the REPL here.
    tryIt.append(el("h3", "lessons__tryit-label", "Try it 🤖"));
    tryIt.append(el("p", "lessons__tryit-prompt", step.tryIt));
    root.append(tryIt);

    // Done marker for this step.
    if (engine.isStepComplete(chapter.id, step.id)) {
      root.append(el("p", "lessons__done-flag", `${COMPLETE_MARKER} Done!`));
    }

    // Navigation controls.
    const nav = el("nav", "lessons__nav");
    nav.setAttribute("aria-label", "Lesson navigation");

    const prev = doc.createElement("button");
    prev.className = "lessons__nav-btn";
    prev.setAttribute("type", "button");
    prev.dataset.action = "prev";
    prev.textContent = "‹ Back";
    if (index <= 0) prev.setAttribute("disabled", "true");
    prev.addEventListener("click", () => engine.prevStep());

    const done = doc.createElement("button");
    done.className = "lessons__nav-btn lessons__nav-btn--done";
    done.setAttribute("type", "button");
    done.dataset.action = "done";
    done.textContent = "I did it! ✓";
    done.addEventListener("click", () => engine.completeCurrentStep());

    const next = doc.createElement("button");
    next.className = "lessons__nav-btn";
    next.setAttribute("type", "button");
    next.dataset.action = "next";
    next.textContent = "Next ›";
    if (index >= total - 1) next.setAttribute("disabled", "true");
    next.addEventListener("click", () => engine.nextStep());

    nav.append(prev, done, next);
    root.append(nav);

    // When the whole chapter is finished, celebrate the payoff.
    if (status.complete) {
      root.append(
        el("p", "lessons__payoff", `🎉 Chapter complete — ${chapter.goal}`),
      );
    }

    return root;
  }

  function render(): void {
    if (!container) return;
    const tree = engine.currentChapter ? renderStep() : renderMenu();
    tree.dataset.lessonsRoot = "true";
    container.replaceChildren(tree);
  }

  return {
    mount(target: HTMLElement) {
      container = target;
      // Idempotent: a re-mount (e.g. the layout re-rendered the tutor pane on a
      // stage switch) drops the stale subscription before re-subscribing, so we
      // never accumulate listeners pointed at detached DOM.
      unsubscribe?.();
      unsubscribe = engine.onChange(render);
      render();
    },
  };
}
